import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import { Typography } from '@/components/ui/typography';
import { useOpeningDetails } from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

import { Card } from './card';

export function GeneralRecommendationContent() {
  const openingSlug = useOpeningSlug();
  const { opening, hiringGoal } = useOpeningDetails();
  const { isActive: isOpeningActive, isSourcingEnabled } = opening;

  const shouldFetch =
    Boolean(hiringGoal?.external_id) &&
    Boolean(hiringGoal?.is_current) &&
    Boolean(opening?.hasActiveHiringGoal);

  if (!shouldFetch) {
    return null;
  }

  if (!isOpeningActive || !isSourcingEnabled) {
    return null;
  }

  const { data } = useQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.generalRecommendation,
      openingSlug,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsGeneralRecommendations(
        openingSlug,
      ),
    enabled: isOpeningActive && isSourcingEnabled,
  });

  const recommendedChannels = useMemo(
    () => data?.recommended_channels ?? [],
    [data],
  );

  if (recommendedChannels.length <= 0) return null;

  return (
    <>
      <Typography as="h2" variant="caption">
        <FormattedMessage
          defaultMessage="General recommendations"
          description="Section heading for org-wide suggested next steps"
        />
      </Typography>
      <div className="flex gap-4">
        <Card channels={recommendedChannels} />
      </div>
    </>
  );
}

export function GeneralRecommendation() {
  return (
    <ErrorBoundary fallback={null}>
      <GeneralRecommendationContent />
    </ErrorBoundary>
  );
}
